import { useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { ProgressBar } from "@/components/ui/progress-bar";
import { cn } from "@/lib/utils";
import type { Peer } from "@/api/generated/model";
import type { ParticipantRow } from "@/components/dashboard/ParticipantsOverviewTable";
import {
  aggregateModuleStats,
  groupAverageProgress,
} from "@/components/dashboard/group-overview/aggregateModuleStats";
import { ModuleAccordionRow } from "@/components/dashboard/group-overview/ModuleAccordionRow";

interface GroupOverviewPanelProps {
  peers: Peer[];
  participants: ParticipantRow[];
  onSelectParticipant: (row: ParticipantRow) => void;
}

const LegendDot = ({
  colorClass,
  label,
}: {
  colorClass: string;
  label: string;
}) => (
  <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
    <span className={cn("size-3 rounded-sm", colorClass)} />
    {label}
  </span>
);

export const GroupOverviewPanel = ({
  peers,
  participants,
  onSelectParticipant,
}: GroupOverviewPanelProps) => {
  const modules = useMemo(
    () => aggregateModuleStats(peers, participants),
    [peers, participants]
  );
  const avg = useMemo(() => groupAverageProgress(peers), [peers]);
  const [openModules, setOpenModules] = useState<Set<number>>(new Set());

  const toggle = (i: number) =>
    setOpenModules((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  const participantCount = participants.length;

  return (
    <Card className="py-3 gap-3 lg:flex-1 lg:min-h-0 lg:overflow-hidden">
      <CardHeader className="pb-0 px-4">
        <div className="flex flex-col gap-2">
          <div>
            <h2 className="text-base font-bold text-primary leading-tight">
              Gruppenübersicht
            </h2>
            <div className="text-xs text-muted-foreground mt-0.5">
              Modulfortschritt aller {participantCount} Lernenden
            </div>
          </div>
          {participantCount > 0 && (
            <div className="flex flex-col gap-1.5 mt-1">
              <div className="flex items-baseline gap-2">
                <span
                  className={cn(
                    "text-3xl font-bold leading-none",
                    avg >= 100 ? "text-success" : "text-secondary"
                  )}
                >
                  {avg}%
                </span>
                <span className="text-xs text-muted-foreground">
                  Ø Gruppenfortschritt
                </span>
              </div>
              <ProgressBar
                percentage={avg}
                size="md"
                color={avg >= 100 ? "success" : "secondary"}
                showLabel={false}
              />
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-2 px-4 lg:flex-1 lg:min-h-0 lg:overflow-y-auto">
        {/* eslint-disable-next-line no-nested-ternary */}
        {participantCount === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            Noch keine Lernenden eingeschrieben.
          </p>
        ) : modules.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            Keine Module verfügbar.
          </p>
        ) : (
          <>
            <h3 className="pt-1 text-sm font-bold text-primary leading-tight">
              Module
            </h3>
            <div className="flex flex-col gap-1">
              {modules.map((m) => (
                <ModuleAccordionRow
                  key={m.title}
                  module={m}
                  isOpen={openModules.has(m.index)}
                  onToggle={() => toggle(m.index)}
                  onSelectParticipant={onSelectParticipant}
                />
              ))}
            </div>
          </>
        )}
      </CardContent>
      {modules.length > 0 && (
        <CardFooter className="px-4 py-1 gap-4 flex-wrap">
          <LegendDot colorClass="bg-[var(--success)]" label="abgeschlossen" />
          <LegendDot colorClass="bg-primary" label="in Bearbeitung" />
          <LegendDot
            colorClass="bg-[var(--muted)]"
            label="noch nicht begonnen"
          />
        </CardFooter>
      )}
    </Card>
  );
};
